/* eslint-disable @typescript-eslint/ban-types */

import './DataTable.css';

import { Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronSort, ChevronUp, Renew } from '@carbon/icons-react';
import {
    ColumnDef,
    RowData,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import React, { Dispatch, ReactElement, ReactNode, SetStateAction, useEffect, useState } from 'react';

import Collapse from './Collapse';

export type ColumnBreakpoints = { [column: string]: 'sm' | 'md' | 'lg' | 'xl' | '2xl' };

interface DataTableProps<T extends Object> {
    data: T[];
    setData?: (data: T[] | ((oldData: T[]) => T[])) => void;
    columns: ColumnDef<T>[];
    expandedContent?(data: T, isExpanded: boolean): React.ReactNode;
    loading: boolean;
    fetchData?: (pageSize: number, pageIndex: number) => void;
    refreshData?: () => void;
    onRecordClick?: (record: T) => void;
    recordsAreClickable?: boolean;
    label?: string;
    hasTopRadius?: boolean;
    columnBreakpoints?: ColumnBreakpoints;
    recordKey?: keyof T;
    selectedRecords?: T[];
    setSelectedRecords?: Dispatch<SetStateAction<T[]>>;
}

declare module '@tanstack/table-core' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface TableMeta<TData extends RowData> {
        updateData?: (rowIndex: number, columnId: string, value: unknown) => void;
    }
}

function DataTable<T extends Object>(props: DataTableProps<T>): ReactElement {
    const {
        data,
        setData,
        columns,
        expandedContent,
        loading,
        fetchData,
        refreshData,
        onRecordClick,
        recordsAreClickable,
        label = 'Records',
        hasTopRadius = true,
        columnBreakpoints,
        recordKey,
        selectedRecords,
        setSelectedRecords,
    } = props;
    const updateData = (rowIndex: number, columnId: string, value: unknown): void => {
        setData &&
            setData((old: T[]) =>
                old.map((row, index) => {
                    if (index === rowIndex) {
                        return {
                            ...old[rowIndex],
                            [columnId]: value,
                        };
                    }
                    return row;
                })
            );
    };

    const table = useReactTable({
        columns: columns,
        data: data,
        autoResetPageIndex: false,
        enableMultiSort: false,
        autoResetAll: false,
        enableExpanding: true,
        enableRowSelection: !!setSelectedRecords,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        meta: {
            updateData,
        },
    });

    const [paginationButtons, setPaginationButtons] = useState<ReactNode[]>([]);

    const getTableCellClass = (columnName: string): string =>
        columnBreakpoints?.[columnName] ? `hidden ${columnBreakpoints[columnName]}:table-cell` : '';

    useEffect(() => {
        fetchData && fetchData(table.getState().pagination.pageSize, table.getState().pagination.pageIndex);
    }, [fetchData, table]);

    // Extracting pagination state into variable to be used in hook dependency array
    const paginationState = table.getState().pagination;
    useEffect(() => {
        const pageButtons: ReactNode[] = [];

        if (paginationState.pageIndex > 2) {
            pageButtons.push(
                <Pagination.Item
                    onClick={() => {
                        table.setPageIndex(0);
                    }}
                    active={paginationState.pageIndex === 0}
                    key="first-page-button"
                >
                    {1}
                </Pagination.Item>
            );
        }
        if (paginationState.pageIndex > 3) {
            pageButtons.push(<Pagination.Ellipsis key="pagination-ellipsis-beginning" disabled />);
        }

        const totalPageCount = table.getPageCount();
        for (let i = 0; i < totalPageCount; i++) {
            if (Math.abs(i - paginationState.pageIndex) <= 2) {
                pageButtons.push(
                    <Pagination.Item
                        onClick={() => {
                            table.setPageIndex(i);
                        }}
                        active={paginationState.pageIndex === i}
                        key={'page-button-' + i}
                        data-testid={`page-button-${i}`}
                    >
                        {i + 1}
                    </Pagination.Item>
                );
            }
        }

        if (totalPageCount - 1 - paginationState.pageIndex > 3) {
            pageButtons.push(<Pagination.Ellipsis key="pagination-ellipsis-end" disabled />);
        }
        if (totalPageCount - 1 - paginationState.pageIndex > 2) {
            pageButtons.push(
                <Pagination.Item
                    onClick={() => {
                        table.setPageIndex(totalPageCount - 1);
                    }}
                    active={paginationState.pageIndex === totalPageCount - 1}
                    key="last-page-button"
                >
                    {totalPageCount}
                </Pagination.Item>
            );
        }

        setPaginationButtons(pageButtons);
    }, [paginationState.pageIndex, table]);

    // Maintains state of selected records when filtering
    useEffect(() => {
        if (recordKey && selectedRecords) {
            selectedRecords.forEach((selectedRecord) => {
                const foundRow = table
                    .getRowModel()
                    .rows.find((row) => row.original[recordKey] === selectedRecord[recordKey]);

                if (foundRow) {
                    foundRow.toggleSelected(true);
                }
            });
        }
    }, [recordKey, selectedRecords, table]);

    return (
        <div className={hasTopRadius ? 'table-container' : 'table-container-without-top-radius'}>
            <Table>
                <thead>
                    <tr>
                        {refreshData && (
                            <th>
                                <Button
                                    variant="link"
                                    title="Refresh table data"
                                    onClick={refreshData}
                                    data-testid="refreshTableButton"
                                >
                                    <Renew size={24}/>
                                </Button>
                            </th>
                        )}
                        {!refreshData && selectedRecords && <th />}
                        {table.getFlatHeaders().map((header) => (
                            <th
                                key={'header-' + header.id}
                                className={`${getTableCellClass(header.id)}${
                                    header.column.getCanSort() ? ' clickable' : ' unset-width'
                                }`}
                                onClick={header.column.getToggleSortingHandler()}
                                data-testid={`header-${header.id}`}
                            >
                                {header.column.getCanSort() && (
                                    <>
                                        {{
                                            asc: <ChevronUp size={24} className="sort-icon" />,
                                            desc: <ChevronDown size={24} className="sort-icon" />,
                                        }[header.column.getIsSorted() as string] ?? (
                                            <ChevronSort size={24} className="sort-icon" />
                                        )}
                                    </>
                                )}
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                        ))}
                    </tr>
                </thead>
                {!loading && data.length ? (
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <React.Fragment key={'row-' + row.index}>
                                    <tr
                                        onClick={(_event: React.MouseEvent) => {
                                            if (onRecordClick) {
                                                onRecordClick(row.original);
                                            }
                                        }}
                                        className={`${recordsAreClickable ? 'navigable-row' : ''} ${
                                            expandedContent ? 'expanded-row' : ''
                                        }`}
                                        data-testid={`row-${row.index}`}
                                    >
                                        {refreshData && !selectedRecords && <td />}
                                        {selectedRecords && setSelectedRecords && recordKey && (
                                            <td>
                                                <input
                                                    title="rowCheckbox"
                                                    className="row-selection"
                                                    type="checkbox"
                                                    checked={row.getIsSelected()}
                                                    onChange={() => {
                                                        if (!row.getIsSelected()) {
                                                            setSelectedRecords((prev) => {
                                                                return [...prev, row.original];
                                                            });
                                                        } else {
                                                            setSelectedRecords((prev) => {
                                                                return prev.filter(
                                                                    (record) =>
                                                                        record[recordKey] !== row.original[recordKey]
                                                                );
                                                            });
                                                        }

                                                        row.toggleSelected();
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    id={`row-checkbox-${row.index}`}
                                                    data-testid={`rowCheckbox-${row.index}`}
                                                />
                                            </td>
                                        )}
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={'cell-' + cell.column.columnDef.header + '-' + row.id}
                                                    className={getTableCellClass(cell.column.id)}
                                                    data-testid={`cell-${cell.column.id}-${row.index}`}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {expandedContent ? (
                                        <tr
                                            className="expanded-row"
                                            style={{ borderTop: !row.getIsExpanded() ? 'none' : '' }}
                                            data-testid={`expanded-row-${row.index}`}
                                        >
                                            <td
                                                colSpan={table.getVisibleFlatColumns().length}
                                                style={{ padding: '0px' }}
                                            >
                                                <Collapse isOpen={row.getIsExpanded()}>
                                                    {expandedContent(row.original, row.getIsExpanded())}
                                                </Collapse>
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                ) : (
                    <tbody>
                        <tr className="table__empty-row">
                            {loading ? (
                                <td
                                    colSpan={
                                        refreshData || setSelectedRecords
                                            ? table.getFlatHeaders().length + 1
                                            : table.getFlatHeaders().length
                                    }
                                    className="spinner-container"
                                    data-testid="loading-spinner"
                                >
                                    <Spinner className="table-spinner" variant="primary" size="sm" animation="border" />
                                </td>
                            ) : (
                                <td
                                    colSpan={
                                        refreshData || setSelectedRecords
                                            ? table.getFlatHeaders().length + 1
                                            : table.getFlatHeaders().length
                                    }
                                    className="table__empty-text"
                                >
                                    No {label} Found
                                </td>
                            )}
                        </tr>
                    </tbody>
                )}
            </Table>
            {!!data.length && (
                <div className="data-table-footer" data-testid="dataTableFooter">
                    <div className="footer-section">
                        <div className="footer-label">Results Per Page</div>
                        <Form.Select
                            id="resultsPerPage"
                            className="page-size"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                            defaultValue={10}
                            data-testid="resultsPerPageSelect"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                        </Form.Select>
                    </div>
                    <div className="footer-section">
                        <div className="footer-label">
                            Page {paginationState.pageIndex + 1} of {table.getPageCount()}
                        </div>
                        <Pagination>
                            <Pagination.Prev
                                onClick={() => {
                                    table.previousPage();
                                }}
                                disabled={!table.getCanPreviousPage()}
                                data-testid="prevPageButton"
                            >
                                <ChevronLeft size={24} />
                            </Pagination.Prev>
                            {paginationButtons}
                            <Pagination.Next
                                onClick={() => {
                                    table.nextPage();
                                }}
                                disabled={!table.getCanNextPage()}
                                data-testid="nextPageButton"
                            >
                                <ChevronRight size={24} />
                            </Pagination.Next>
                        </Pagination>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
